import {
  Configuration,
  PublicClientApplication,
  EventType,
  AccountInfo,
  AuthenticationResult,
  BrowserAuthError,
  BrowserAuthErrorCodes,
  InteractionRequiredAuthError
} from "@azure/msal-browser";

import { getEmbedConfig } from "@/lib/embed/config";
import {
  getPortalAccessToken,
  invalidatePortalToken,
  isPortalTokenEnabled,
} from "@/lib/embed/portalTokenProvider";
import { getTeamsConfig } from "@/lib/teams/config";

export const APPREG_SCOPE = import.meta.env.VITE_APPREG_SCOPE;

/**
 * Redirect URI MSAL uses for the auth response. A dedicated, blank
 * `/auth-redirect` route keeps the redirect handling out of the app shell
 * (and works for both redirect and popup flows).
 */
export function authRedirectUri(): string {
  return `${window.location.origin}/auth-redirect`;
}

/**
 * Read a Vite env var, treating missing, empty, and unreplaced
 * `DUMMY_<key>` placeholder tokens as "not set" (undefined).
 */
function readEnv(key: string): string | undefined {
  const value = import.meta.env[key];
  if (value === undefined || value === "" || value === `DUMMY_${key}`) {
    return undefined;
  }
  return value;
}

/**
 * How interactive auth fallbacks are performed. Popups keep the flow inside
 * the iframe (embed mode); redirects are the default for the standalone app.
 * Overridable via `VITE_AUTH_INTERACTION` ("popup" | "redirect").
 */
export function getAuthInteraction(): "redirect" | "popup" {
  const override = readEnv("VITE_AUTH_INTERACTION");
  if (override === "popup" || override === "redirect") {
    return override;
  }
  return getEmbedConfig().embedMode ? "popup" : "redirect";
}

export const configuration: Configuration = {
  auth: {
    clientId: import.meta.env.VITE_APPREG_CLIENT_ID,
    authority: import.meta.env.VITE_APPREG_AUTHORITY,
    redirectUri: authRedirectUri(),
    postLogoutRedirectUri: window.location.origin,
  },
};

export const msalInstance = new PublicClientApplication(configuration);

const cachedAccounts = msalInstance.getAllAccounts();
if (cachedAccounts.length > 0) {
  msalInstance.setActiveAccount(cachedAccounts[0]);
}

msalInstance.addEventCallback((event) => {
  if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
    const account = (event.payload as AuthenticationResult).account as
      | AccountInfo
      | undefined;
    if (account) {
      msalInstance.setActiveAccount(account);
    }
  }
});

/**
 * Browser-level failures of the hidden renewal iframe that mean the same thing
 * as `InteractionRequiredAuthError`: the silent path is finished and only a
 * prompt can recover.
 *
 * `acquireTokenSilent` does not only POST the refresh token -- when that token
 * is expired or invalid it falls back to `acquireTokenBySilentIframe`, which
 * loads our own /auth-redirect in a hidden same-origin iframe. Three ways that
 * comes back unusable, none of them an `InteractionRequiredAuthError`:
 *
 *   monitor_window_timeout  the frame was refused or never answered, so MSAL
 *                           waited out its 10s iframeHashTimeout. A CSP without
 *                           `'self'` in frame-ancestors does exactly this --
 *                           MA-248, and worth keeping handled even now that the
 *                           header is fixed.
 *   hash_empty_error        the frame loaded but the response fragment was gone
 *   hash_does_not_contain_known_properties   ...or was not a response at all
 *
 * Deliberately a code allow-list, NOT `instanceof BrowserAuthError`. That class
 * is a grab-bag, and prompting is the wrong answer for most of it:
 * `interaction_in_progress` would tear down an interaction that is already
 * running, and `redirect_in_iframe`, `block_iframe_reload` and
 * `uninitialized_public_client_application` are all bugs a sign-in cannot fix.
 * `auth.test.ts` pins both directions -- do not "simplify" this to the class.
 *
 * Kept a pure predicate: the warning that accompanies a recovery belongs to the
 * caller that actually recovers, not to the test that classifies the error.
 */
const IFRAME_RENEWAL_DEAD_ENDS: readonly string[] = [
  BrowserAuthErrorCodes.monitorWindowTimeout,
  BrowserAuthErrorCodes.hashEmptyError,
  BrowserAuthErrorCodes.hashDoesNotContainKnownProperties,
];

function isIframeRenewalDeadEnd(e: unknown): boolean {
  return (
    e instanceof BrowserAuthError && IFRAME_RENEWAL_DEAD_ENDS.includes(e.errorCode)
  );
}

export const acquireAccessToken = async () => {
  // In a Teams build, tokens come from the Teams host (NAA / SSO), not the
  // browser MSAL redirect/popup flow. Dynamic import avoids a static import
  // cycle: teams/auth imports `configuration` and `APPREG_SCOPE` from here.
  if (getTeamsConfig().teamsEnabled) {
    const { acquireTeamsAccessToken } = await import("@/lib/teams/auth");
    return acquireTeamsAccessToken();
  }

  // In an embed build with the MA-227 flag on, the host portal can hand us a
  // token for our own API, which spares the user the one-time in-frame sign-in
  // popup. Second branch of the same shape as the Teams one above, and for the
  // same reason: tokens do not come from browser MSAL on this path either.
  //
  // STATIC import here, unlike Teams. The dynamic import above exists solely to
  // break an import cycle -- `teams/auth` imports `configuration` and
  // `APPREG_SCOPE` from this file. The portal provider imports neither; it reads
  // its resource and tenant from its own env vars. So there is no cycle to break
  // and nothing to gain from deferring the module. Do not make these "consistent".
  //
  // Falls through rather than throwing when the portal has nothing for us: the
  // provider returns `undefined` for every failure it can have, and MSAL below is
  // the fallback the gate then drives with a popup.
  //
  // ONLY WHEN MSAL HOLDS NO ACCOUNT, and this condition is load-bearing security,
  // not an optimisation. Without it the portal token outranks an MSAL session the
  // user established deliberately, and this is reachable:
  //
  //   the exchange times out on mount (a timeout is not a 401, so the provider's
  //   latch never sets) -> the gate falls back to the button -> the user signs in
  //   via `loginPopup({ prompt: "select_account" })`, possibly as a DIFFERENT
  //   account -> the next API call asks the portal again, the exchange now
  //   succeeds, and the portal session's token wins over the account the user
  //   just chose.
  //
  // Nothing surfaces the mismatch: the embed renders no account name, and the
  // backend takes identity from the token's `oid`. The user would then read and
  // write the PORTAL session's conversation history and file TOPdesk tickets
  // under that identity. MBOA is a school with shared machines, so "the portal
  // session and the chosen account are the same person" is not a safe assumption.
  //
  // It costs nothing in UX, which is the other half of why this is the right
  // shape. A user with a cached MSAL account gets a silent `acquireTokenSilent`
  // below anyway -- no popup, no prompt -- so the popup-free experience is
  // preserved for exactly the users the portal path exists for: the ones with no
  // account in this browser profile yet.
  if (isPortalTokenEnabled() && msalInstance.getAllAccounts().length === 0) {
    const token = await getPortalAccessToken();
    if (token) return token;
  }

  const activeAccount = msalInstance.getActiveAccount();
  const accounts = msalInstance.getAllAccounts();

  const accountToUse = activeAccount ?? accounts[0];
  if (!accountToUse) {
    throw new Error("User is not signed in");
  }

  try {
    const authResult = await msalInstance.acquireTokenSilent({
      scopes: [APPREG_SCOPE],
      account: accountToUse,
    });
    return authResult.accessToken;
  } catch (e) {
    // Not silently recoverable, but still a defect worth seeing: the renewal
    // should normally succeed, and prompting hides from us that it did not.
    const deadEnd = isIframeRenewalDeadEnd(e);
    if (deadEnd) {
      console.warn(
        `Silent token renewal failed (${(e as BrowserAuthError).errorCode}); falling back to an interactive sign-in`,
      );
    }

    if (e instanceof InteractionRequiredAuthError || deadEnd) {
      if (getAuthInteraction() === "popup") {
        const result = await msalInstance.acquireTokenPopup({
          scopes: [APPREG_SCOPE],
          account: accountToUse,
        });
        return result.accessToken;
      }
      await msalInstance.acquireTokenRedirect({
        scopes: [APPREG_SCOPE],
        account: accountToUse,
      });
      return "";
    }
    throw e;
  }
};

/**
 * Drop credentials the backend has just refused, and report whether there were
 * any to drop.
 *
 * Exists for exactly one caller: `fetchWithAuth`, on a 401/403, deciding whether
 * a single retry could possibly help. It can only help on the portal path, where
 * "the token is stale" and "the portal should not be asked again" are both true
 * and both actionable -- invalidating drops the cached token AND latches the path
 * off, so the retry goes out on MSAL instead (see `invalidatePortalToken`).
 *
 * Returns false on every other build and path, which is what keeps a 401 on an
 * MSAL or Teams token behaving byte-for-byte as it does today: one toast, one
 * `notifyAuthenticationRequired`, no second request. There is nothing to
 * invalidate there in any case -- MSAL owns its own cache and the Teams host owns
 * the broker's.
 */
export function invalidateAccessToken(): boolean {
  return isPortalTokenEnabled() ? invalidatePortalToken() : false;
}
